import { useMemo } from "react";
import { graphql, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { isEmpty } from "lodash";
import { useCompetitionEditPageQuery } from "./loaders/CompetitionEditPage";
import * as v from "valibot";
import { valibotResolver } from "@hookform/resolvers/valibot";
import ErrorPage from "@/pages/ErrorPage";
import {
  extractUploadables,
  preprocessEdit,
  relayErrorMessage,
} from "@/utils/relay";
import { CompetitionEditPageMutation as CompetitionEditPageMutationType } from "./__generated__/CompetitionEditPageMutation.graphql";
import { useIntl, FormattedMessage } from "react-intl";
import {
  GeneralSection,
  SubmissionSection,
  TagsSection,
  generalSchemaFactory,
  submissionSchema,
  tagsSchema,
} from "@/components/ActivityEdit";
import CompetitionDeleteButton from "@/components/CompetitionDeleteButton";
import { toast } from "@/utils/toast";
import { logger } from "@/common/logger";
import { Form } from "@/kit/ui/form";
import { Button } from "@/kit/ui/button";
import { useFetchFile } from "@/utils/hooks";

const CompetitionEditPageMutation = graphql`
  mutation CompetitionEditPageMutation(
    $id: ID!
    $input: UpdateCompetitionInput!
  ) {
    updateCompetition(id: $id, input: $input) {
      node {
        id
        slug
        title
        shortDescription
        banner
        visibility
        thumbnail
        requiresApproval
        noCode
        useJuryScore
        showMetric
        tags {
          ...CompetitionTagInputForCompetitionEditPageFragment
        }
      }
    }
  }
`;

export default function CompetitionEditPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    query: { competitionBySlug: competition, ...query },
  } = useCompetitionEditPageQuery();

  const formSchema = useMemo(
    () =>
      v.intersect([
        generalSchemaFactory(intl),
        submissionSchema(intl),
        tagsSchema,
      ]),
    [intl],
  );

  const files = {
    banner: useFetchFile(competition?.banner),
    thumbnail: useFetchFile(competition?.thumbnail),
  };
  const form = useForm<v.InferOutput<typeof formSchema>>({
    values: competition
      ? {
          ...competition,
          tagIds: competition.tags.nodes.map((tag) => tag.id),
          banner: files.banner.file,
          thumbnail: files.thumbnail.file,
        }
      : undefined,
    resolver: valibotResolver(formSchema),
  });

  const [commitMutation, isMutationInFlight] =
    useMutation<CompetitionEditPageMutationType>(CompetitionEditPageMutation);

  if (!competition) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({
          defaultMessage: "Competition not found",
        })}
      />
    );
  }

  const onSubmit = (data: v.InferOutput<typeof formSchema>) => {
    const {
      variables: { input },
      uploadables,
    } = extractUploadables({
      input: preprocessEdit(data, form.formState.defaultValues),
    });
    if (isEmpty(input)) {
      navigate(`/competitions/${competition.slug}`);
    }
    commitMutation({
      variables: { id: competition.id, input },
      uploadables,
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
      onCompleted: (results) => {
        navigate(`/competitions/${results.updateCompetition.node.slug}`);
      },
    });
  };

  return (
    <div className="pb-8 space-y-8">
      <Form form={form} onSubmit={onSubmit}>
        <div className="space-y-8">
          <div className="flex justify-between">
            <div>
              <h1 className="text-xl font-semibold">
                <FormattedMessage defaultMessage="Edit Competition" />
              </h1>
              <p className="text-sm text-grey-500">
                <FormattedMessage defaultMessage="Manage competition options" />
              </p>
            </div>
            <div>
              <Button type="submit" disabled={isMutationInFlight}>
                <FormattedMessage defaultMessage="Save" />
              </Button>
            </div>
          </div>
          <div className="space-y-8">
            <GeneralSection routePrefix="competitions" />
            <TagsSection tags={query} competitionTags={competition.tags} />
            <div>
              <h2 className="text-lg font-semibold">
                <FormattedMessage defaultMessage="Submission" />
              </h2>
              <p className="text-sm text-grey-500">
                <FormattedMessage defaultMessage="Manage rules for submissions" />
              </p>
            </div>
            <SubmissionSection />
          </div>
        </div>
      </Form>
      <CompetitionDeleteButton id={competition.id} title={competition.title} />
    </div>
  );
}
